import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

const BASE_W = 390;
const BASE_H = 285;


const BOX_RECTS_BASE = [
    [21, 74, 83, 127],
    [92, 74, 154, 127],
    [163, 74, 226, 127],
    [234, 74, 297, 127],
    [306, 74, 368, 127],
    [21, 135, 83, 188],
    [92, 135, 154, 188],
    [163, 135, 226, 188],
    [234, 135, 297, 188],
    [306, 135, 368, 188],
];

const COMPLETED_BOX_BASE = {
    x: 152,
    y: 259,
    w: 130,
    h: 18,
};

const SPEND_BUTTON_BASE = {
    x: 276,
    y: 260,
    w: 72,
    h: 18,
};

function mulberry32(a) {
    return function () {
        let t = (a += 0x6d2b79f5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

function hashSeedToInt(str) {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

export default function StampCard({
                                      userData,
                                      stamps = 0,
                                      completedCards = 0,
                                      loading = false,
                                      blankSrc = `${process.env.PUBLIC_URL}/img/frampcard_blank.png`,
                                      stampSrc = `${process.env.PUBLIC_URL}/img/stampcard_stamp.png`,
                                      className = "",
                                  }) {
    const canvasRef = useRef(null);
    const [size, setSize] = useState({ w: 0, h: 0 });
    const [renderScale, setRenderScale] = useState({ sx: 1, sy: 1 });
    const [activeInfo, setActiveInfo] = useState(null);

    const progress = stamps % 10;
    const completedCount = completedCards;

    const boxRectsNorm = useMemo(() => {
        return BOX_RECTS_BASE.map(([x0, y0, x1, y1]) => [
            x0 / BASE_W,
            y0 / BASE_H,
            x1 / BASE_W,
            y1 / BASE_H,
        ]);
    }, []);

    const completedBoxNorm = useMemo(() => {
        return {
            x: COMPLETED_BOX_BASE.x / BASE_W,
            y: COMPLETED_BOX_BASE.y / BASE_H,
            w: COMPLETED_BOX_BASE.w / BASE_W,
            h: COMPLETED_BOX_BASE.h / BASE_H,
        };
    }, []);

    const spendBtnNorm = useMemo(() => {
        return {
            x: SPEND_BUTTON_BASE.x / BASE_W,
            y: SPEND_BUTTON_BASE.y / BASE_H,
            w: SPEND_BUTTON_BASE.w / BASE_W,
            h: SPEND_BUTTON_BASE.h / BASE_H,
        };
    }, []);

    const infoIconsNorm = useMemo(() => {
        const icons = [
            { x: 60,  y: 44,  text: "Complete this framp card by being active throughout the Frankly-verse!" },
            { x: 18,  y: 210, text: "Up and down vote articles, leave comments, use our features, engage in the Discord! Every interaction has a chance to gain a framp, use those completed framp cards to enter giveaways, competitions and win rewards!" },
            { x: 18,  y: 268, text: `Completed Framp Cards shows how many full framp cards of 10 you have earned. Use the Redeem button to spend them in the Framp Shack.\n\nCompleted Framp Cards: ${completedCards}\nCurrent Card: ${progress}/10` },
        ];
        return icons.map(({ x, y, text }) => ({
            x: x / BASE_W,
            y: y / BASE_H,
            text,
        }));
    }, [stamps, completedCards, progress]);

    useEffect(() => {
        if (loading) return;

        let cancelled = false;

        const blankImg = new Image();
        const stampImg = new Image();
        blankImg.crossOrigin = "anonymous";
        stampImg.crossOrigin = "anonymous";

        const load = (img) =>
            new Promise((resolve, reject) => {
                img.onload = () => resolve(img);
                img.onerror = reject;
            });

        blankImg.src = blankSrc;
        stampImg.src = stampSrc;

        Promise.all([load(blankImg), load(stampImg)])
            .then(([blank, stamp]) => {
                if (cancelled) return;

                const canvas = canvasRef.current;
                if (!canvas) return;

                const w = blank.naturalWidth;
                const h = blank.naturalHeight;

                canvas.width = w;
                canvas.height = h;
                setSize({ w, h });

                const ctx = canvas.getContext("2d");
                if (!ctx) return;

                ctx.imageSmoothingEnabled = false;

                ctx.clearRect(0, 0, w, h);
                ctx.drawImage(blank, 0, 0);

                const seed = userData?.discordId || "guest";
                const rng = mulberry32(hashSeedToInt(`${seed}|${progress}`));

                for (let i = 0; i < progress; i++) {
                    const [nx0, ny0, nx1, ny1] = boxRectsNorm[i];
                    const x0 = Math.round(nx0 * w);
                    const y0 = Math.round(ny0 * h);
                    const x1 = Math.round(nx1 * w);
                    const y1 = Math.round(ny1 * h);

                    const boxW = x1 - x0;
                    const boxH = y1 - y0;

                    const ox = Math.round((rng() * 2 - 1) * 4);
                    const oy = Math.round((rng() * 2 - 1) * 4);
                    const rot = (rng() * 2 - 1) * (Math.PI / 18);

                    const cx = x0 + boxW / 2 + ox;
                    const cy = y0 + boxH / 2 + oy;

                    ctx.save();
                    ctx.translate(cx, cy);
                    ctx.rotate(rot);
                    ctx.imageSmoothingEnabled = false;
                    ctx.drawImage(stamp, -stamp.naturalWidth / 2, -stamp.naturalHeight / 2);
                    ctx.restore();
                }

                requestAnimationFrame(() => {
                    const r = canvas.getBoundingClientRect();
                    const sx = r.width / canvas.width || 1;
                    const sy = r.height / canvas.height || 1;
                    setRenderScale({ sx, sy });
                });
            })
            .catch(() => {});

        return () => {
            cancelled = true;
        };
    }, [
        blankSrc,
        stampSrc,
        progress,
        completedCount,
        userData?.discordId,
        boxRectsNorm,
        completedBoxNorm,
        loading
    ]);

    const completedCountStyle = useMemo(() => {
        const left = size.w ? Math.round(completedBoxNorm.x * size.w * renderScale.sx) : 0;
        const top = size.h ? Math.round(completedBoxNorm.y * size.h * renderScale.sy) : 0;
        const width = size.w ? Math.round(completedBoxNorm.w * size.w * renderScale.sx) : 0;
        const height = size.h ? Math.round(completedBoxNorm.h * size.h * renderScale.sy) : 0;
        return { left, top, width, height };
    }, [size.w, size.h, completedBoxNorm, renderScale]);

    const completedFontPx = useMemo(() => {
        if (!size.w) return 12;
        const scale = (size.w / BASE_W) * renderScale.sx;
        return Math.max(10, Math.round(22 * scale));
    }, [size.w, renderScale.sx]);

    const spendStyle = useMemo(() => {
        const left = size.w ? Math.round(spendBtnNorm.x * size.w * renderScale.sx) : 0;
        const top = size.h ? Math.round(spendBtnNorm.y * size.h * renderScale.sy) : 0;
        const width = size.w ? Math.round(spendBtnNorm.w * size.w * renderScale.sx) : 0;
        const height = size.h ? Math.round(spendBtnNorm.h * size.h * renderScale.sy) : 0;

        return { left, top, width, height };
    }, [size.w, size.h, spendBtnNorm, renderScale]);

    const redeemFontPx = useMemo(() => {
        if (!size.w) return 12;
        const scale = (size.w / BASE_W) * renderScale.sx;
        return Math.max(10, Math.round(14 * scale));
    }, [size.w, renderScale.sx]);

    if (loading) {
        return (
            <div className={`w-fit ${className}`}>
                <div className="w-[390px] h-[285px] bg-gray-200 animate-pulse rounded" />
            </div>
        );
    }

    return (
        <div className={`relative w-fit ${className}`}>
            <canvas
                ref={canvasRef}
                style={{
                    width: size.w ? `${size.w}px` : undefined,
                    height: size.h ? `${size.h}px` : undefined,
                    imageRendering: "pixelated",
                    display: "block",
                }}
            />

            {size.w > 0 && size.h > 0 && infoIconsNorm.map((icon, i) => {
                const left = Math.round(icon.x * size.w * renderScale.sx);
                const top = Math.round(icon.y * size.h * renderScale.sy);
                return (
                    <button
                        key={i}
                        onClick={() => setActiveInfo(icon.text)}
                        style={{ position: "absolute", left, top, transform: "translate(-50%, -50%)" }}
                        className="w-4 h-4 rounded-full bg-maroon text-offwhite flex items-center justify-center leading-none select-none hover:opacity-80"
                        aria-label="Read text aloud"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
                            <circle cx="8" cy="4.5" r="1.25" />
                            <rect x="6.75" y="7" width="2.5" height="5.5" rx="1.25" />
                        </svg>
                    </button>
                );
            })}

            {activeInfo && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    onClick={() => setActiveInfo(null)}
                >
                    <div className="absolute inset-0 bg-black/50" />
                    <div
                        className="relative bg-white rounded-lg shadow-xl p-6 max-w-sm w-full"
                        onClick={e => e.stopPropagation()}
                    >
                        <p className="text-lg text-gray-800 whitespace-pre-line">{activeInfo}</p>
                        <button
                            onClick={() => setActiveInfo(null)}
                            className="mt-4 text-sm text-gray-500 underline"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {size.w > 0 && size.h > 0 && (
                <div
                    style={{
                        position: "absolute",
                        left: completedCountStyle.left,
                        top: completedCountStyle.top,
                        width: completedCountStyle.width,
                        height: completedCountStyle.height,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                    }}
                >
                    <span
                        className="font-['Jersey_10'] leading-none select-none"
                        style={{ color: "#700000", fontSize: `${completedFontPx}px` }}
                    >
                        {completedCount}
                    </span>
                </div>
            )}

            {size.w > 0 && size.h > 0 && (
                <Link
                    to="/members/frampshack"
                    style={{
                        position: "absolute",
                        ...spendStyle,
                    }}
                    className="flex items-center justify-center select-none"
                >
                    <span
                        className="font-[Jersey_10] font-bold text-offwhite bg-maroon rounded-[3px] px-3 py-[2px] leading-none select-none"
                        style={{ fontSize: `${redeemFontPx}px` }}
                    >
                        Redeem!
                    </span>
                </Link>
            )}
        </div>
    );
}